import {
  Autocomplete,
  Button,
  IconButton,
  type Item,
  SelectInput,
  SelectInputItem,
  TextField,
} from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useGetApiV1LocationCitiesDropdown,
  useGetApiV1LocationCountriesDropdown,
  useGetApiV1LocationStatesDropdown,
} from '@/shared/api/generated';
import { FormField } from '@/shared/ui/molecules';

import type { AddressFormValues } from '../personFormSchema';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type AddressPrefix = 'personalAddress' | 'paymentAddress';

export interface AddressModalProps {
  readonly open: boolean;
  readonly prefix: AddressPrefix | null;
  readonly defaultValues: AddressFormValues;
  readonly onClose: () => void;
  readonly onSave: (prefix: AddressPrefix, values: AddressFormValues) => void;
}

const BRAZIL_ID = 1;

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Modal de criação e edição de endereço da pessoa.
 *
 * @param open - Controla visibilidade
 * @param prefix - Tipo de endereço sendo editado (personal | payment)
 * @param defaultValues - Valores atuais do endereço para pré-preencher
 * @param onClose - Callback ao fechar sem salvar
 * @param onSave - Callback com o prefixo e os valores preenchidos
 */
export function AddressModal({ open, prefix, defaultValues, onClose, onSave }: AddressModalProps) {
  const { t } = useTranslation('personManagement');

  const { register, handleSubmit, reset, control, setValue } = useForm<AddressFormValues>({
    defaultValues,
  });

  useEffect(() => {
    if (open) reset(defaultValues);
  }, [open, defaultValues, reset]);

  const countryId = useWatch({ control, name: 'countryId' });
  const stateId = useWatch({ control, name: 'stateId' });
  const cityId = useWatch({ control, name: 'cityId' });
  const isBrazil = countryId === BRAZIL_ID;

  const { data: countryOptions = [] } = useGetApiV1LocationCountriesDropdown();
  const { data: stateOptions = [] } = useGetApiV1LocationStatesDropdown(
    countryId !== undefined && countryId !== null ? { countryId } : undefined,
    { query: { enabled: !!countryId && isBrazil } },
  );
  const { data: cityOptions = [] } = useGetApiV1LocationCitiesDropdown(
    stateId !== undefined && stateId !== null ? { stateId } : undefined,
    { query: { enabled: !!stateId && isBrazil } },
  );

  const countryItems: Item<number>[] = countryOptions.map(({ key, value }) => ({
    label: value ?? '',
    value: key ?? 0,
  }));
  const stateItems: Item<number>[] = stateOptions.map(({ key, value }) => ({
    label: value ?? '',
    value: key ?? 0,
  }));
  const cityItems: Item<number>[] = cityOptions.map(({ key, value }) => ({
    label: value ?? '',
    value: key ?? 0,
  }));

  const selectedCountry = countryItems.find(i => i.value === countryId) ?? null;
  const selectedState = stateItems.find(i => i.value === stateId) ?? null;
  const selectedCity = cityItems.find(i => i.value === cityId) ?? null;

  const onSubmit = (values: AddressFormValues) => {
    if (prefix) onSave(prefix, values);
    onClose();
  };

  const typeOptions: Array<{ value: AddressPrefix; label: string }> = [
    {
      value: 'personalAddress',
      label: t('personForm.address.personalAddress'),
    },
    { value: 'paymentAddress', label: t('personForm.address.paymentAddress') },
  ];

  const title = prefix
    ? t('personForm.address.editAddress', {
        type: typeOptions.find(o => o.value === prefix)?.label ?? '',
      })
    : t('personForm.address.addAddress');

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && onClose()} modal={false}>
      <Dialog.Portal>
        <div className="fixed inset-none z-40 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex overflow-y-auto max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
          onPointerDownOutside={e => {
            const target = e.target as Element;
            if (target.closest('[data-headlessui-portal]')) e.preventDefault();
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-mini">
            <Dialog.Title className="ts-heading-sm text-ctx-content-title">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.cancel')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <form
            id="address-modal-form"
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-xs px-xs py-mini"
          >
            {/* CEP + País */}
            <div className="grid grid-cols-1 gap-xs md:grid-cols-2">
              <FormField label={t('personForm.address.zipCode')}>
                <TextField fullWidth size="md" placeholder="00000-000" {...register('zipCode')} />
              </FormField>
              <Autocomplete<number>
                size="md"
                label={t('personForm.address.country')}
                placeholder={t('personForm.address.country')}
                custom={{
                  optionList: 'z-[9999]',
                }}
                list={countryItems}
                {...(selectedCountry !== null ? { selectedItem: selectedCountry } : {})}
                handleChange={item => {
                  setValue('countryId', item?.value ?? undefined);
                  setValue('stateId', undefined);
                  setValue('cityId', undefined);
                  setValue('stateName', null);
                  setValue('cityName', null);
                }}
              />
            </div>

            {/* Estado + Cidade */}
            <div className="grid grid-cols-1 gap-xs md:grid-cols-2">
              {isBrazil ? (
                <Autocomplete<number>
                  size="md"
                  label={t('personForm.address.state')}
                  placeholder={t('personForm.address.state')}
                  custom={{
                    optionList: 'z-[9999]',
                  }}
                  list={stateItems}
                  {...(selectedState !== null ? { selectedItem: selectedState } : {})}
                  handleChange={item => {
                    setValue('stateId', item?.value ?? undefined);
                    setValue('cityId', undefined);
                    setValue('cityName', null);
                  }}
                />
              ) : (
                <FormField label={t('personForm.address.state')}>
                  <TextField
                    fullWidth
                    size="md"
                    placeholder={t('personForm.address.state')}
                    {...register('stateName')}
                  />
                </FormField>
              )}
              {isBrazil ? (
                <Autocomplete<number>
                  size="md"
                  label={t('personForm.address.city')}
                  placeholder={t('personForm.address.city')}
                  custom={{
                    optionList: 'z-[9999]',
                  }}
                  list={cityItems}
                  {...(selectedCity !== null ? { selectedItem: selectedCity } : {})}
                  handleChange={item => {
                    setValue('cityId', item?.value ?? undefined);
                  }}
                />
              ) : (
                <FormField label={t('personForm.address.city')}>
                  <TextField
                    fullWidth
                    size="md"
                    placeholder={t('personForm.address.city')}
                    {...register('cityName')}
                  />
                </FormField>
              )}
            </div>

            {/* Endereço + Número */}
            <div className="grid grid-cols-1 gap-xs md:grid-cols-4">
              <div className="md:col-span-3">
                <FormField label={t('personForm.address.street')}>
                  <TextField
                    fullWidth
                    size="md"
                    placeholder={t('personForm.address.street')}
                    {...register('street')}
                  />
                </FormField>
              </div>
              <FormField label={t('personForm.address.placeNumber')}>
                <TextField fullWidth size="md" placeholder="123" {...register('placeNumber')} />
              </FormField>
            </div>

            {/* Bairro + Complemento */}
            <div className="grid grid-cols-1 gap-xs md:grid-cols-2">
              <FormField label={t('personForm.address.neighborhood')}>
                <TextField
                  fullWidth
                  size="md"
                  placeholder={t('personForm.address.neighborhood')}
                  {...register('neighborhood')}
                />
              </FormField>
              <FormField label={t('personForm.address.complement')}>
                <TextField
                  fullWidth
                  size="md"
                  placeholder={t('personForm.address.complement')}
                  {...register('complement')}
                />
              </FormField>
            </div>

            {/* Tipo (somente no modo adicionar) */}
            {!prefix && (
              <FormField label={t('personForm.address.type')}>
                <SelectInput
                  size="md"
                  placeholder={t('personForm.address.type')}
                  value=""
                  onValueChange={() => undefined}
                >
                  {typeOptions.map(o => (
                    <SelectInputItem key={o.value} value={o.value} custom={{ root: 'text-nowrap' }}>
                      {o.label}
                    </SelectInputItem>
                  ))}
                </SelectInput>
              </FormField>
            )}
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-xs py-mini">
            <Button hierarchy="secondary" size="lg" type="button" onClick={onClose}>
              {t('actions.cancel')}
            </Button>
            <Button hierarchy="primary" size="lg" type="submit" form="address-modal-form">
              {t('actions.save')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
